import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import 'xterm/css/xterm.css';

export default function SSHTerminal({ host, onClose, macMini, sshUsername, sshPassword }) {
  const [pos, setPos] = useState({ x: 120, y: 120 });
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const wsRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    // 1) Init xterm with scrollback + fit addon
    const term = new Terminal({
      cursorBlink: true,
      convertEol: true,
      scrollback: 2000,
      theme: { background: '#0a0a0a', foreground: '#33ff33' },
      scrollOnUserInput: true
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    term.focus();

    // 2) Whenever xterm itself resizes, scroll to bottom
    const sendResize = () => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      }
    };
    term.onResize(({ cols, rows }) => {
      // xterm already applied the resize; just notify backend
      sendResize();
      term.scrollToBottom();
    });

    // 3) Do an initial fit (this will fire onResize → scrollToBottom)
    fit.fit();
    setTimeout(sendResize, 0);

    // 4) When the outer wrapper changes size (CSS resize), refit
    const handleResize = () => {
      requestAnimationFrame(() => {
        fit.fit();
        sendResize();
      });
    };
    const ro = new ResizeObserver(handleResize);
    ro.observe(wrapperRef.current);

    // fallback: window resize
    window.addEventListener('resize', handleResize);

    termRef.current = term;

    let query = `/api/ssh?host=${host}`;

    if (macMini) {
      query += `&macMini=true`;
    }
    if (sshUsername) {
      query += `&sshUsername=${encodeURIComponent(sshUsername)}`;
    }
    if (sshPassword) {
      query += `&sshPassword=${encodeURIComponent(sshPassword)}`;
    }

    // 5) Wire up SSH WebSocket
    const ws = new WebSocket(
      (window.location.protocol === 'https:' ? 'wss' : 'ws') + '://' +
      window.location.host + query
    );
    wsRef.current = ws;

    const hb = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping', t: Date.now() }));
      };
    }, 30000);

    ws.onopen = () => {
      term.writeln(`*** connecting to host: ${host} ***`);
      term.scrollToBottom();
      sendResize();
    };
    ws.onmessage = e => {
      term.write(e.data);
      term.scrollToBottom();
    };
    ws.onclose = ({ code, reason }) => {
      console.log("Closing SSHTerminal")
      term.writeln(`\r\n*** disconnected (code=${code} reason=${reason}) ***`);
      term.scrollToBottom();
    };
    ws.onerror = () => {
      console.log("SSHTerminal socket error")
      term.writeln('\r\n*** socket error ***');
      term.scrollToBottom();
    };
    term.onData(d => {
      if (ws.readyState === WebSocket.OPEN) ws.send(d);
    });

    // cleanup
    return () => {
      clearInterval(hb);
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
      ws.close();
      term.dispose();
    };
  }, [host]);

  // Drag-to-move
  useEffect(() => {
    const onMouseMove = e => {
      if (dragRef.current) {
        const { dx, dy } = dragRef.current;
        setPos({ x: e.clientX - dx, y: e.clientY - dy });
      }
    };
    const onMouseUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startDrag = e => {
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: 800,
        height: 450,
        resize: 'both',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 30
      }}
      className="rounded-lg border border-neutral-700 shadow-lg select-none"
    >
      <div
        onMouseDown={startDrag}
        className="bg-neutral-800 text-neutral-300 text-sm cursor-move flex justify-between items-center p-2"
      >
        <span>SSH Terminal</span>
        <button
          onClick={onClose}
          className="ml-2 text-neutral-400 hover:text-neutral-600 focus:outline-none"
        >
          ✕
        </button>
      </div>
      <div
        ref={containerRef}
        style={{
          flex: 1,
          outline: 'none',
          position: 'relative'
        }}
      />
    </div>
  );
}